import { useEffect, useState } from "react";
import { Play, Youtube, ArrowUpRight, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { SOCIALS } from "@/data/site";
import { Vinyl } from "@/components/podcast/StudioGear";

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  const d = Math.floor(diffSec / 86400);
  if (d > 365) return `${Math.floor(d / 365)}y ago`;
  if (d > 30) return `${Math.floor(d / 30)}mo ago`;
  if (d > 0) return `${d}d ago`;
  const h = Math.floor(diffSec / 3600);
  if (h > 0) return `${h}h ago`;
  const m = Math.floor(diffSec / 60);
  return `${m}m ago`;
}

export default function YouTubeClips({ limit = 6 }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openVid, setOpenVid] = useState(null);

  useEffect(() => {
    let active = true;
    api
      .get(`/youtube/latest?limit=${limit}`)
      .then((r) => {
        if (!active) return;
        if (r.data.error) setError(r.data.error);
        setVideos(r.data.videos || []);
      })
      .catch(() => active && setError("fetch_failed"))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [limit]);

  return (
    <section
      data-testid="youtube-clips"
      className="relative py-20 md:py-32 bg-[#0A0A0A] border-y border-[#262626] overflow-hidden film-grain"
    >
      {/* Spinning vinyl flare */}
      <Vinyl
        className="absolute -right-32 top-1/2 -translate-y-1/2 w-[28rem] hidden lg:block spin-slow"
        color="#E10600"
        strokeWidth={1}
        style={{ opacity: 0.06 }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase font-bold mb-3 flex items-center gap-2">
              <Youtube className="w-4 h-4 text-[#FF0000]" />
              <span className="text-[#FF0000]">Live From The Channel</span>
            </p>
            <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tight leading-none">
              Latest <span className="text-[#E10600]">Clips</span> &{" "}
              <span className="text-[#1E6FE0]">Shorts</span>
            </h2>
            <p className="mt-4 text-zinc-400 max-w-xl text-sm md:text-base">
              Auto-syncs from{" "}
              <a
                href={SOCIALS.youtube}
                target="_blank"
                rel="noreferrer"
                className="text-white hover:text-[#E10600] underline"
              >
                @theproveyourpointpodcast
              </a>{" "}
              every 30 minutes. New drops appear here automatically.
            </p>
          </div>
          <a
            href={SOCIALS.youtube}
            target="_blank"
            rel="noreferrer"
            data-testid="youtube-clips-view-channel"
            className="btn-primary"
          >
            Watch The Channel
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>

        {loading && (
          <p className="text-zinc-500 font-mono text-sm">Pulling latest from YouTube…</p>
        )}

        {!loading && error && (
          <div className="border border-[#262626] p-6 text-zinc-400 text-sm">
            Couldn't load videos right now.{" "}
            <a
              href={SOCIALS.youtube}
              target="_blank"
              rel="noreferrer"
              className="text-[#E10600] underline"
            >
              View the channel on YouTube
            </a>
            .
          </div>
        )}

        {!loading && !error && videos.length === 0 && (
          <p className="text-zinc-500 font-mono text-sm">No videos yet — check back soon.</p>
        )}

        {!loading && videos.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#262626] border border-[#262626]">
            {videos.map((v, i) => (
              <button
                key={v.video_id}
                data-testid={`youtube-clip-${i}`}
                onClick={() => setOpenVid(v)}
                className="group text-left bg-[#0A0A0A] hover:bg-[#121212] transition-colors overflow-hidden"
              >
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={v.thumbnail}
                    alt={v.title}
                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  {/* YouTube tag */}
                  <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/70 px-2 py-1 border border-[#FF0000]/50">
                    <Youtube className="w-3.5 h-3.5 text-[#FF0000]" />
                    <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-white">
                      YouTube
                    </span>
                  </div>
                  {/* Play overlay */}
                  <div className="absolute bottom-3 right-3 w-12 h-12 bg-[#E10600]/0 border border-white/40 flex items-center justify-center group-hover:bg-[#E10600] group-hover:border-[#E10600] transition-all">
                    <Play className="w-5 h-5 text-white" fill="currentColor" />
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-display text-xl uppercase leading-tight text-white group-hover:text-[#E10600] transition-colors line-clamp-2">
                    {v.title}
                  </h3>
                  <p className="mt-2 text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-mono flex items-center gap-2">
                    <Clock className="w-3 h-3" />
                    {timeAgo(v.published_at)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Player modal */}
      {openVid && (
        <div
          data-testid="youtube-clip-modal"
          className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-md p-4 flex items-center justify-center"
          onClick={() => setOpenVid(null)}
        >
          <div
            className="bg-[#0A0A0A] border border-[#E10600] max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="aspect-video w-full bg-black">
              <iframe
                title={openVid.title}
                src={`https://www.youtube.com/embed/${openVid.video_id}?autoplay=1`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
                data-testid="youtube-clip-player"
              />
            </div>
            <div className="p-6 md:p-8">
              <h2 className="font-display text-3xl md:text-4xl uppercase leading-tight">
                {openVid.title}
              </h2>
              <p className="mt-3 text-zinc-400 text-sm">
                Published {timeAgo(openVid.published_at)}
              </p>
              {openVid.description && (
                <p className="mt-4 text-sm text-zinc-300 whitespace-pre-line">
                  {openVid.description}
                </p>
              )}
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={openVid.url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary"
                >
                  Watch on YouTube
                  <Youtube className="w-4 h-4" />
                </a>
                <button
                  data-testid="youtube-clip-modal-close"
                  onClick={() => setOpenVid(null)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
